import { useMemo } from "react"
import { useGet } from "../utils/apiComponent/useApi"
import { endpoints } from "../utils/endpoints"
import { quizzesApiResponseSchema } from "../utils/schemas/quiz-schema"

/**
 * Hook to fetch quiz for a specific module
 * @param {string} moduleId - The ID of the module to fetch quiz for
 * @returns {Object} - { quiz, loading, error, refetch }
 */
export function useQuiz(moduleId) {
  // Memoize the endpoint to prevent unnecessary re-renders
  const endpoint = useMemo(() => {
    return moduleId ? `${endpoints.quiz}?moduleId=${moduleId}` : null
  }, [moduleId])
  
  const shouldFetch = Boolean(moduleId)
  
  const {
    data: quizzesData,
    loading,
    error,
    execute: refetch
  } = useGet(endpoint || `${endpoints.quiz}?moduleId=placeholder`, {
    schema: quizzesApiResponseSchema
  })

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(() => {
    const quizzes = shouldFetch ? (quizzesData?.quizzes || []) : []
    const quiz = quizzes.length > 0 ? quizzes[0] : null // Assume one quiz per module
    
    return {
      quiz,
      quizzes,
      loading: shouldFetch ? loading : false,
      error: shouldFetch ? error : null,
      refetch: shouldFetch ? refetch : () => {},
      total: shouldFetch ? (quizzesData?.total || 0) : 0,
      page: shouldFetch ? (quizzesData?.page || 1) : 1,
      totalPages: shouldFetch ? (quizzesData?.totalPages || 1) : 1
    }
  }, [shouldFetch, quizzesData, loading, error, refetch])
}

/**
 * Hook to fetch all quizzes (without module filter)
 * @returns {Object} - { quizzes, loading, error, refetch }
 */
export function useAllQuizzes() {
  const {
    data: quizzesData,
    loading,
    error,
    execute: refetch
  } = useGet(endpoints.quiz, {
    schema: quizzesApiResponseSchema
  })

  return {
    quizzes: quizzesData?.quizzes || [],
    loading,
    error,
    refetch,
    total: quizzesData?.total || 0,
    page: quizzesData?.page || 1,
    totalPages: quizzesData?.totalPages || 1
  }
}
